import { __decorate } from "tslib";
import { Group, MathUtils } from 'three';
import { CachedMeasure } from '../../Core/CachedMeasure.js';
import { Save, SaveChild } from '../../Core/Save.js';
import { ApexSign, ViewType } from '../../Models/AppModel.js';
import { SelectedApexMech } from '../../States/State.js';
import { Consts } from '../../Utils/Consts.js';
import { LimitUtils } from '../../Utils/LimitUtils.js';
import { Utils } from '../../Utils/Utils.js';
import { VectorUtils } from '../../Utils/VectorUtils.js';
import { LimitLine } from '../Primitive/LimitLine.js';
import { FemurAnalyzerAPAnatomical } from './FemurAnalyzerAPAnatomical.js';
import { FemurAnalyzerAPMechanical } from './FemurAnalyzerAPMechanical.js';
export class MechanicalAxisFemurAP extends Group {
    onAfterRestore() {
        this.updateNSATool();
        this.update();
    }
    get angleForTemplating() {
        return VectorUtils.linesAngleFromOrigin(this.mechanical.FH.position.clone().sub(this.mechanical.CE.position), Consts.verDir);
    }
    get LPFAStandard() {
        return this._mLPFAStandard;
    }
    set LPFAStandard(value) {
        this._mLPFAStandard = value;
        this.update();
    }
    set LPFAStandardDegrees(value) {
        this._mLPFAStandard = MathUtils.degToRad(value);
        this.update();
    }
    get mLDFAStandard() {
        return this._mLDFAStandard;
    }
    set mLDFAStandard(value) {
        this._mLDFAStandard = value;
        this.update();
    }
    set mLDFAStandardDegrees(value) {
        this._mLDFAStandard = MathUtils.degToRad(value);
        this.update();
    }
    get apexWithSign() {
        return this.apexAngle.value * this.apexAngleSign.value * (this._isLeft ? 1 : -1);
    }
    get proximalDirToCalculateApex() {
        return this.mechanical.GT.position.clone().sub(this.mechanical.FH.position).applyAxisAngle(this._axisForRotation, Math.PI - this._mLPFAStandard);
    }
    get distalDirToCalculateApex() {
        return this.mechanical.LE.position.clone().sub(this.mechanical.ME.position).applyAxisAngle(this._axisForRotation, this._mLDFAStandard);
    }
    get isApexValid() {
        return this._isApexValid;
    }
    get FH() {
        return this.mechanical.FH;
    }
    get CE() {
        return this.mechanical.CE;
    }
    get apexBisector() {
        return this.mechanical.apexBisector;
    }
    get mechAxisUpperPoint() {
        return this.FH.position;
    }
    get mechAxisLowerPoint() {
        return this.CE.position;
    }
    constructor(isLeft, isContralateral, isEOC) {
        super();
        this.pointsToRegister = 4;
        this._limitLines = [];
        this._enableLimits = false; // refresh page to make it works (to improve performance when don't use it)
        this.name = 'FemurAnalyzer';
        this._isContralateral = isContralateral;
        this._isLeft = isLeft;
        this._axisForRotation = isLeft ? Consts.planeNormal.clone() : Consts.planeNormal.clone().multiplyScalar(-1);
        this._isEOC = isEOC;
        if (this._enableLimits) {
            this._limitLines[0] = new LimitLine(ViewType.AP, 0xffff00);
            this._limitLines[1] = new LimitLine(ViewType.AP, 0x00ffff);
            this._limitLines[2] = new LimitLine(ViewType.AP, 0xff00ff);
            this._limitLines[3] = new LimitLine(ViewType.AP, 0xffffff);
            this._limitLines[4] = new LimitLine(ViewType.AP, 0xaaaa00);
            this._limitLines[5] = new LimitLine(ViewType.AP, 0x00aaaa);
            this._limitLines[6] = new LimitLine(ViewType.AP, 0xaa00aa);
            this._limitLines[7] = new LimitLine(ViewType.AP, 0xaaaaaa);
        }
        this.add(this.mechanical = new FemurAnalyzerAPMechanical(isLeft, isContralateral, this._limitLines), this.anatomical = new FemurAnalyzerAPAnatomical(isLeft, isContralateral, this.mechanical.FH));
        this._mLPFAStandard = MathUtils.degToRad(Consts.paleyAngles.AP.mLPFA.standard);
        this._mLDFAStandard = MathUtils.degToRad(Consts.paleyAngles.AP.mLDFA.standard);
        this.bindEvents();
        this.LPFA = new CachedMeasure(() => {
            return this.mechanical.GT.position.clone().sub(this.mechanical.FH.position).angleTo(this.mechanical.CE.position.clone().sub(this.mechanical.FH.position));
        }, this.mechanical.GT, this.mechanical.FH, this.mechanical.CE);
        this.mLDFA = new CachedMeasure(() => {
            return this.mechanical.LE.position.clone().sub(this.mechanical.CE.position).angleTo(this.mechanical.FH.position.clone().sub(this.mechanical.CE.position));
        }, this.mechanical.LE, this.mechanical.CE, this.mechanical.FH);
        this.aLDFA = new CachedMeasure(() => {
            return this.mechanical.GT.position.clone().sub(this.mechanical.ACE.position).angleTo(this.mechanical.LE.position.clone().sub(this.mechanical.ME.position));
        }, this.mechanical.GT, this.mechanical.CE, this.mechanical.LE, this.mechanical.ME);
        this.MNSA = new CachedMeasure(() => {
            return this.mechanical.FH.position.clone().sub(this.anatomical.NS_FH_C.position).angleTo(this.anatomical.NS_GT_lower_C.position.clone().sub(this.anatomical.NS_GT_upper_C.position));
        }, this.mechanical.FH, this.anatomical.NS_FH_C, this.anatomical.NS_GT_lower_C, this.anatomical.NS_GT_upper_C);
        this.MPFA = new CachedMeasure(() => {
            return this.mechanical.FH.position.clone().sub(this.mechanical.GT.position).angleTo(this.anatomical.NS_GT_lower_C.position.clone().sub(this.anatomical.NS_GT_PointForLineUpper.position));
        }, this.mechanical.FH, this.mechanical.GT, this.anatomical.NS_GT_lower_C, this.anatomical.NS_GT_PointForLineUpper);
        this.length = new CachedMeasure(() => {
            return this.mechanical.FH.position.distanceTo(this.mechanical.CE.position);
        }, this.mechanical.FH, this.mechanical.CE);
        if (!this._isContralateral) {
            this.apexAngleToFix = new CachedMeasure(() => {
                return this.mechanical.apex.visible ? this.getApexAngle() : 0;
            }, this.mechanical.apex);
            this.apexAngle = new CachedMeasure(() => {
                return this.apexAngleToFix.value > Math.PI / 2 ? Math.PI - this.apexAngleToFix.value : this.apexAngleToFix.value;
            }, this.mechanical.apex);
            this.apexAngleSign = new CachedMeasure(() => {
                if (this.mechanical.apex.visible) {
                    const perpDir = this.mechanical.CE.position.clone().sub(this.mechanical.apex.position).cross(this._axisForRotation);
                    return Math.sign(this.mechanical.apex.position.clone().sub(this.mechanical.FH.position).dot(perpDir)) * (this.apexAngleToFix.value > Math.PI / 2 ? -1 : 1);
                }
                return ApexSign.valgus;
            }, this.mechanical.apex);
        }
    }
    bindEvents() {
        this.bindEvent("onAfterDragMove", () => {
            this.update();
        });
        this.bindEvent("onDragEnd", () => {
            this.clearLimits();
        });
        this.bindDragLimit_GT();
        this.bindDragLimit_FH();
        this.bindDragLimit(this.mechanical.LE, this.mechanical.ME.position, this.mechanical.GT.position, this.mechanical.FH.position);
        this.bindDragLimit(this.mechanical.ME, this.mechanical.LE.position, this.mechanical.FH.position, this.mechanical.GT.position);
        this.bindDragLimit_NS_GT_upper(this.anatomical.NS_GT_upper_A, this.anatomical.NS_GT_upper_B);
        this.bindDragLimit_NS_GT_upper(this.anatomical.NS_GT_upper_B, this.anatomical.NS_GT_upper_A);
        this.bindDragLimit_NS_GT_upper_center();
        this.bindDragLimit_NS_GT_lower(this.anatomical.NS_GT_lower_A, this.anatomical.NS_GT_lower_B);
        this.bindDragLimit_NS_GT_lower(this.anatomical.NS_GT_lower_B, this.anatomical.NS_GT_lower_A);
        this.bindDragLimit_NS_GT_lower_center();
        this.bindDragLimit_NS_FH(this.anatomical.NS_FH_A, this.anatomical.NS_FH_B);
        this.bindDragLimit_NS_FH(this.anatomical.NS_FH_B, this.anatomical.NS_FH_A);
        this.bindDragLimit_NS_FH_center();
    }
    bindDragLimit_GT() {
        this.mechanical.GT.bindEvent('onDragMove', (args) => {
            for (let i = 0; i < 2; i++) { //2 cycles removes projection multi limit errors
                LimitUtils.applyLimitAndProjectVector(this.mechanical.FH.position, this.mechanical.ME.position, args.position, this.mechanical.LE.position, this.mechanical.GT.scale.x, this._limitLines[i * 4 + 0]);
                LimitUtils.applyLimitAndProjectVector(this.mechanical.ME.position, this.mechanical.LE.position, args.position, this.mechanical.FH.position, this.mechanical.GT.scale.x, this._limitLines[i * 4 + 1]);
                LimitUtils.applyLimitAndProjectVector(this.mechanical.FH.position, this.mechanical.LE.position, args.position, this.mechanical.FH.position.clone().add(this.mechanical.FH.position.clone().sub(this.mechanical.ME.position)), this.mechanical.GT.scale.x, this._limitLines[i * 4 + 2]);
            }
            const pointForLine = VectorUtils.lines2DIntersection(this.anatomical.NS_GT_lower_C.position, this.anatomical.NS_GT_upper_C.position, args.position, this.mechanical.FH.position);
            pointForLine && this.anatomical.NS_GT_PointForLineUpper.position.copy(pointForLine);
        });
    }
    bindDragLimit_FH() {
        this.mechanical.FH.bindEvent('onDragMove', (args) => {
            for (let i = 0; i < 2; i++) { //2 cycles removes projection multi limit errors
                LimitUtils.applyLimitAndProjectVector(this.mechanical.GT.position, this.mechanical.LE.position, args.position, this.mechanical.ME.position, this.mechanical.FH.scale.x, this._limitLines[i * 4 + 0]);
                LimitUtils.applyLimitAndProjectVector(this.mechanical.LE.position, this.mechanical.ME.position, args.position, this.mechanical.GT.position, this.mechanical.FH.scale.x, this._limitLines[i * 4 + 1]);
                LimitUtils.applyLimitAndProjectVector(this.mechanical.GT.position, this.mechanical.ME.position, args.position, this.mechanical.GT.position.clone().add(this.mechanical.GT.position.clone().sub(this.mechanical.LE.position)), this.mechanical.FH.scale.x, this._limitLines[i * 4 + 2]);
            }
            const pointForLine = VectorUtils.lines2DIntersection(this.anatomical.NS_GT_lower_C.position, this.anatomical.NS_GT_upper_C.position, this.mechanical.GT.position, args.position);
            pointForLine && this.anatomical.NS_GT_PointForLineUpper.position.copy(pointForLine);
            const pointForLine2 = VectorUtils.lines2DIntersection(args.position, this.anatomical.NS_FH_C.position, this.anatomical.NS_GT_lower_C.position, this.anatomical.NS_GT_upper_C.position);
            pointForLine2 && this.anatomical.NS_FH_PointForLine.position.copy(pointForLine2);
        });
    }
    bindDragLimit(point, nearConsecutive, farConsecutive, diagonal) {
        point.bindEvent('onDragMove', (args) => {
            for (let i = 0; i < 2; i++) { //2 cycles removes projection multi limit errors
                LimitUtils.applyLimitAndProjectVector(nearConsecutive, diagonal, args.position, farConsecutive, point.scale.x, this._limitLines[i * 4 + 0]);
                LimitUtils.applyLimitAndProjectVector(diagonal, farConsecutive, args.position, nearConsecutive, point.scale.x, this._limitLines[i * 4 + 1]);
                LimitUtils.applyLimitAndProjectVector(nearConsecutive, farConsecutive, args.position, nearConsecutive.clone().add(nearConsecutive.clone().sub(diagonal)), point.scale.x, this._limitLines[i * 4 + 2]);
                this._LP && LimitUtils.applyLimitAndProjectVector(this._LP, this._MP, args.position, this.mechanical.FH.position, point.scale.x, this._limitLines[i * 4 + 3]);
            }
        });
    }
    bindDragLimit_NS_GT_upper(point, oppositePoint) {
        point.bindEvent('onDragMove', (args) => {
            LimitUtils.applyMultiLimits(args.position, point.position, [
                { a: this.mechanical.GT.position, b: this.mechanical.FH.position },
                { a: this.anatomical.NS_GT_lower_A.position, b: this.anatomical.NS_GT_lower_B.position },
            ]);
            this.anatomical.NS_GT_upper_C.position.copy(args.position.clone().lerp(oppositePoint.position, 0.5));
            const pointForLine = VectorUtils.lines2DIntersection(this.anatomical.NS_GT_lower_C.position, this.anatomical.NS_GT_upper_C.position, this.mechanical.GT.position, this.mechanical.FH.position);
            pointForLine && this.anatomical.NS_GT_PointForLineUpper.position.copy(pointForLine);
            const pointForLine2 = VectorUtils.lines2DIntersection(this.mechanical.FH.position, this.anatomical.NS_FH_C.position, this.anatomical.NS_GT_lower_C.position, this.anatomical.NS_GT_upper_C.position);
            pointForLine2 && this.anatomical.NS_FH_PointForLine.position.copy(pointForLine2);
        });
    }
    bindDragLimit_NS_GT_upper_center() {
        this.anatomical.NS_GT_upper_C.bindEvent('onDragMove', (args) => {
            const anat = this.anatomical, mech = this.mechanical;
            args.position.copy(VectorUtils.projectOnVector(args.position, this.anatomical.NS_GT_lower_C.position, this.anatomical.NS_GT_upper_C.position));
            LimitUtils.applyLimitAndSetVectorCenterLinePoint(mech.FH.position, mech.GT.position, anat.NS_GT_upper_C.position, args.position, anat.NS_GT_upper_A.position, anat.NS_GT_upper_B.position);
            LimitUtils.applyLimitAndSetVectorCenterLinePoint(anat.NS_GT_lower_A.position, anat.NS_GT_lower_B.position, anat.NS_GT_upper_C.position, args.position, anat.NS_GT_upper_A.position, anat.NS_GT_upper_B.position);
            const pan = args.position.clone().sub(this.anatomical.NS_GT_upper_C.position);
            this.anatomical.NS_GT_upper_A.position.copy(this.anatomical.NS_GT_upper_A.position.add(pan));
            this.anatomical.NS_GT_upper_B.position.copy(this.anatomical.NS_GT_upper_B.position.add(pan));
        });
    }
    bindDragLimit_NS_GT_lower(point, oppositePoint) {
        point.bindEvent('onDragMove', (args) => {
            LimitUtils.applyMultiLimits(args.position, point.position, [
                { a: this.anatomical.NS_FH_A.position, b: this.anatomical.NS_FH_B.position },
                { a: this.anatomical.NS_GT_upper_A.position, b: this.anatomical.NS_GT_upper_B.position },
            ]);
            this.anatomical.NS_GT_lower_C.position.copy(args.position.clone().lerp(oppositePoint.position, 0.5));
            const pointForLine = VectorUtils.lines2DIntersection(this.anatomical.NS_GT_lower_C.position, this.anatomical.NS_GT_upper_C.position, this.mechanical.GT.position, this.mechanical.FH.position);
            pointForLine && this.anatomical.NS_GT_PointForLineUpper.position.copy(pointForLine);
            const pointForLine2 = VectorUtils.lines2DIntersection(this.mechanical.FH.position, this.anatomical.NS_FH_C.position, this.anatomical.NS_GT_lower_C.position, this.anatomical.NS_GT_upper_C.position);
            pointForLine2 && this.anatomical.NS_FH_PointForLine.position.copy(pointForLine2);
        });
    }
    bindDragLimit_NS_GT_lower_center() {
        this.anatomical.NS_GT_lower_C.bindEvent('onDragMove', (args) => {
            const anat = this.anatomical;
            args.position.copy(VectorUtils.projectOnVector(args.position, this.anatomical.NS_GT_lower_C.position, this.anatomical.NS_GT_upper_C.position));
            LimitUtils.applyLimitAndSetVectorCenterLinePoint(anat.NS_FH_A.position, anat.NS_FH_B.position, anat.NS_GT_lower_C.position, args.position, anat.NS_GT_lower_A.position, anat.NS_GT_lower_B.position);
            LimitUtils.applyLimitAndSetVectorCenterLinePoint(anat.NS_GT_upper_A.position, anat.NS_GT_upper_B.position, anat.NS_GT_lower_C.position, args.position, anat.NS_GT_lower_A.position, anat.NS_GT_lower_B.position);
            const pan = args.position.clone().sub(this.anatomical.NS_GT_lower_C.position);
            this.anatomical.NS_GT_lower_A.position.copy(this.anatomical.NS_GT_lower_A.position.add(pan));
            this.anatomical.NS_GT_lower_B.position.copy(this.anatomical.NS_GT_lower_B.position.add(pan));
        });
    }
    bindDragLimit_NS_FH(point, oppositePoint) {
        point.bindEvent('onDragMove', (args) => {
            LimitUtils.applyMultiLimits(args.position, point.position, [
                { a: this.mechanical.FH.position, b: this.mechanical.GT.position },
                { a: this.anatomical.NS_GT_lower_A.position, b: this.anatomical.NS_GT_lower_B.position },
                { a: this.anatomical.NS_GT_upper_C.position, b: this.anatomical.NS_GT_lower_C.position },
            ]);
            this.anatomical.NS_FH_C.position.copy(args.position.clone().lerp(oppositePoint.position, 0.5));
            const pointForLine = VectorUtils.lines2DIntersection(this.mechanical.FH.position, this.anatomical.NS_FH_C.position, this.anatomical.NS_GT_lower_C.position, this.anatomical.NS_GT_upper_C.position);
            pointForLine && this.anatomical.NS_FH_PointForLine.position.copy(pointForLine);
        });
    }
    bindDragLimit_NS_FH_center() {
        this.anatomical.NS_FH_C.bindEvent("onDragMove", (args) => {
            const anat = this.anatomical, mech = this.mechanical;
            args.position.copy(VectorUtils.projectOnVector(args.position, mech.FH.position, anat.NS_FH_PointForLine.position));
            LimitUtils.applyLimitAndSetVectorCenterLinePoint(mech.FH.position, mech.GT.position, anat.NS_FH_C.position, args.position, anat.NS_FH_A.position, anat.NS_FH_B.position);
            LimitUtils.applyLimitAndSetVectorCenterLinePoint(anat.NS_GT_upper_C.position, anat.NS_GT_lower_C.position, anat.NS_FH_C.position, args.position, anat.NS_FH_A.position, anat.NS_FH_B.position);
            LimitUtils.applyLimitAndSetVectorCenterLinePoint(anat.NS_GT_lower_A.position, anat.NS_GT_lower_B.position, anat.NS_FH_C.position, args.position, anat.NS_FH_A.position, anat.NS_FH_B.position);
            const pan = args.position.clone().sub(anat.NS_FH_C.position);
            anat.NS_FH_A.position.copy(anat.NS_FH_A.position.add(pan));
            anat.NS_FH_B.position.copy(anat.NS_FH_B.position.add(pan));
        });
    }
    restorePositions(positions) {
        this.mechanical.GT.position.copy(positions[0]);
        this.mechanical.FH.position.copy(positions[1]);
        this.mechanical.LE.position.copy(positions[2]);
        this.mechanical.ME.position.copy(positions[3]);
        this.anatomical.NS_FH_A.position.copy(positions[4]);
        this.anatomical.NS_FH_B.position.copy(positions[5]);
        this.anatomical.NS_GT_lower_A.position.copy(positions[6]);
        this.anatomical.NS_GT_lower_B.position.copy(positions[7]);
        this.anatomical.NS_GT_upper_A.position.copy(positions[8]);
        this.anatomical.NS_GT_upper_B.position.copy(positions[9]);
        this.updateNSATool();
        this.update();
    }
    initPositions(positions) {
        this.fixPositionsOrder(positions);
        this.mechanical.GT.position.copy(positions[0].setZ(0));
        this.mechanical.FH.position.copy(positions[1].setZ(0));
        this.mechanical.LE.position.copy(positions[2].setZ(0));
        this.mechanical.ME.position.copy(positions[3].setZ(0));
        this.set_CE_ACE_Position();
        const dirAngle = this.mechanical.GT.position.clone().sub(this.mechanical.FH.position).applyAxisAngle(this._axisForRotation, Math.PI / -4);
        const NS = VectorUtils.lines2DIntersection(this.mechanical.FH.position, this.mechanical.FH.position.clone().add(dirAngle), this.mechanical.ACE.position, this.mechanical.GT.position);
        const length = this.mechanical.FH.position.distanceTo(this.mechanical.GT.position) / 4;
        const NS_FH_perp = VectorUtils.getPerpendicular(NS.clone().sub(this.mechanical.FH.position)).setLength(length);
        const NS_GT_perp = VectorUtils.getPerpendicular(NS.clone().sub(this.mechanical.GT.position)).setLength(length);
        // GT to FH 1/2 length
        const NS_FH_C_alpha = 1 - (2 * length / NS.distanceTo(this.mechanical.FH.position));
        this.anatomical.NS_FH_C.position.copy(NS.clone().lerp(this.mechanical.FH.position, NS_FH_C_alpha));
        this.anatomical.NS_FH_A.position.copy(this.anatomical.NS_FH_C.position).add(NS_FH_perp);
        this.anatomical.NS_FH_B.position.copy(this.anatomical.NS_FH_C.position).sub(NS_FH_perp);
        // GT to FH 1.5 length
        const NS_GT_lower_C_alpha = 1 - 1.5 * (4 * length / NS.distanceTo(this.mechanical.GT.position));
        this.anatomical.NS_GT_lower_C.position.copy(NS.clone().lerp(this.mechanical.GT.position, NS_GT_lower_C_alpha));
        this.anatomical.NS_GT_lower_A.position.copy(this.anatomical.NS_GT_lower_C.position).add(NS_GT_perp);
        this.anatomical.NS_GT_lower_B.position.copy(this.anatomical.NS_GT_lower_C.position).sub(NS_GT_perp);
        this.anatomical.NS_GT_upper_C.position.copy(this.anatomical.NS_GT_lower_C.clone().position.lerp(this.mechanical.GT.position, 0.25));
        this.anatomical.NS_GT_upper_A.position.copy(this.anatomical.NS_GT_upper_C.position).add(NS_GT_perp);
        this.anatomical.NS_GT_upper_B.position.copy(this.anatomical.NS_GT_upper_C.position).sub(NS_GT_perp);
        this.update();
        const point = VectorUtils.lines2DIntersection(this.mechanical.FH.position, this.anatomical.NS_FH_C.position, this.anatomical.NS_GT_lower_C.position, this.anatomical.NS_GT_upper_C.position);
        this.anatomical.NS_FH_PointForLine.position.copy(point);
        const pointForLine = VectorUtils.lines2DIntersection(this.anatomical.NS_GT_lower_C.position, this.anatomical.NS_GT_upper_C.position, this.mechanical.GT.position, this.mechanical.FH.position);
        this.anatomical.NS_GT_PointForLineUpper.position.copy(pointForLine);
    }
    updateNSATool() {
        this.anatomical.NS_FH_C.position.copy(this.anatomical.NS_FH_A.position.clone().lerp(this.anatomical.NS_FH_B.position, 0.5));
        this.anatomical.NS_GT_lower_C.position.copy(this.anatomical.NS_GT_lower_A.position.clone().lerp(this.anatomical.NS_GT_lower_B.position, 0.5));
        this.anatomical.NS_GT_upper_C.position.copy(this.anatomical.NS_GT_upper_A.position.clone().lerp(this.anatomical.NS_GT_upper_B.position, 0.5));
        const point = VectorUtils.lines2DIntersection(this.mechanical.FH.position, this.anatomical.NS_FH_C.position, this.anatomical.NS_GT_lower_C.position, this.anatomical.NS_GT_upper_C.position);
        point && this.anatomical.NS_FH_PointForLine.position.copy(point);
        const pointForLine = VectorUtils.lines2DIntersection(this.anatomical.NS_GT_lower_C.position, this.anatomical.NS_GT_upper_C.position, this.mechanical.GT.position, this.mechanical.FH.position);
        pointForLine && this.anatomical.NS_GT_PointForLineUpper.position.copy(pointForLine);
    }
    fixPositionsOrder(positions) {
        const middlePointA = positions[0].clone().lerp(positions[1], 0.5);
        const middlePointB = positions[2].clone().lerp(positions[3], 0.5);
        const perpDir = middlePointA.clone().sub(middlePointB).cross(Consts.planeNormal);
        const sign = this._isLeft ? -1 : 1;
        if (Math.sign(positions[0].clone().sub(middlePointA).dot(perpDir)) === sign) {
            Utils.swapItemInArray(0, 1, positions);
        }
        if (Math.sign(positions[2].clone().sub(middlePointA).dot(perpDir)) === sign) {
            Utils.swapItemInArray(2, 3, positions);
        }
    }
    update() {
        this.set_CE_ACE_Position();
        if (!this._isContralateral) {
            const mLPFAValid = Utils.isRadiantsAngleInDegreesRange(this.LPFA.value, Consts.paleyAngles.AP.mLPFA.min, Consts.paleyAngles.AP.mLPFA.max);
            const mLDFAValid = Utils.isRadiantsAngleInDegreesRange(this.mLDFA.value, Consts.paleyAngles.AP.mLDFA.min, Consts.paleyAngles.AP.mLDFA.max);
            const invalidAngles = !mLPFAValid || !mLDFAValid;
            const apex = VectorUtils.lines2DIntersection(this.mechanical.FH.position, this.mechanical.FH.position.clone().add(this.proximalDirToCalculateApex), this.mechanical.CE.position, this.mechanical.CE.position.clone().add(this.distalDirToCalculateApex));
            apex && this.mechanical.apex.position.copy(apex);
            this._isApexValid = invalidAngles && apex !== undefined;
            this.setApexVisibility(invalidAngles && apex !== undefined);
        }
        this.dispatchEvent({ type: 'updated' });
    }
    set_CE_ACE_Position() {
        this.mechanical.CE.position.copy(this.mechanical.LE.position.clone().lerp(this.mechanical.ME.position, 0.5));
        this.mechanical.ACE.position.copy(this.mechanical.CE.position.clone().add(this.mechanical.ME.position.clone().sub(this.mechanical.LE.position).setLength(10)));
    }
    setApexVisibility(visible) {
        if (this._isEOC && (this.selectedApex === SelectedApexMech.tibiaDistal || this.selectedApex === SelectedApexMech.tibiaProximal)) {
            visible = false;
        }
        if (this.mechanical.apex.visible !== visible) {
            this.mechanical.apex.visible = visible;
            this.mechanical.apexLabel.visible = visible;
            this.mechanical.proximalMechanicalAxis.visible = visible;
            this.mechanical.distalMechanicalAxis.visible = visible;
        }
    }
    clearLimits() {
        for (const limitLine of this._limitLines) {
            limitLine.visible = false;
        }
    }
    setTibiaLimits(LP, MP) {
        this._LP = LP;
        this._MP = MP;
    }
    /**
    * Calculates apex angle.
    */
    getApexAngle() {
        return this.mechanical.FH.position.clone().sub(this.mechanical.apex.position).angleTo(this.mechanical.apex.position.clone().sub(this.mechanical.CE.position));
    }
}
__decorate([
    SaveChild()
], MechanicalAxisFemurAP.prototype, "anatomical", void 0);
__decorate([
    SaveChild()
], MechanicalAxisFemurAP.prototype, "mechanical", void 0);
__decorate([
    Save()
], MechanicalAxisFemurAP.prototype, "selectedApex", void 0);
__decorate([
    Save()
], MechanicalAxisFemurAP.prototype, "_mLPFAStandard", void 0);
__decorate([
    Save()
], MechanicalAxisFemurAP.prototype, "_mLDFAStandard", void 0);
//# sourceMappingURL=FemurAnalyzerAP.js.map